import { Component } from '@theme/component';

/**
 * Kauf-Section: Mengenauswahl, die Preis und Formular mitführt.
 *
 * Die Chips sind **keine** Radiobuttons im Markup, sondern echte Buttons mit
 * `aria-pressed` — Horizons Varianten-Picker ist hier bewusst nicht im Spiel,
 * weil er die Preisstaffel nicht je Option anzeigt.
 *
 * ⚠️ Alle Preise stehen bereits serverseitig im Markup (`data-price`), aus
 * `variant.price | money`. Nichts wird im Browser gerechnet oder formatiert:
 * Währung, Trennzeichen und Steuerhinweis hängen am Markt des Besuchers, und
 * das weiss nur Shopify. Der Prototyp hat hier clientseitig formatiert und
 * lief damit an marktkorrekten Preisen vorbei.
 *
 * @typedef {object} BuyRefs
 * @property {HTMLInputElement} variantId
 * @property {HTMLElement} price
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement[]} [chips]
 *
 * @extends {Component<BuyRefs>}
 */
export class BuyBox extends Component {
  requiredRefs = ['variantId', 'price', 'submit'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
    if (!chip || !this.contains(chip)) return;

    this.#select(chip);
  };

  /** @param {Element} chip */
  #select(chip) {
    const { variantId, price, submit } = this.refs;

    for (const other of this.querySelectorAll('[data-variant-id]')) {
      const active = other === chip;
      other.classList.toggle('buy__chip--active', active);
      other.setAttribute('aria-pressed', String(active));
    }

    variantId.value = chip.getAttribute('data-variant-id') ?? '';
    price.textContent = chip.getAttribute('data-price') ?? '';

    // Je Variante steht ein Bild im Markup (`sections/buy-box.liquid`), schon in
    // der Verpackung des Lieferlandes. Hier wird nur umgeschaltet.
    //
    // ⚠️ Die Bilder stehen in `.buy__media`, also NEBEN `<buy-box>`, nicht darin.
    // `this.querySelectorAll` fände keins und schaltete still nichts um — deshalb
    // über die gemeinsame Zeile `.buy__inner` suchen, die genau eine Section umfasst.
    const row = this.closest('.buy__inner') ?? this;
    for (const image of row.querySelectorAll('[data-variant-image]')) {
      if (image instanceof HTMLElement) image.hidden = image.dataset.variantImage !== variantId.value;
    }

    // Ausverkaufte Mengen lassen sich wählen, aber nicht bestellen — sonst
    // wäre unklar, warum eine Menge fehlt.
    const available = chip.getAttribute('data-available') === 'true';
    submit.disabled = !available;
    setLabel(
      submit,
      available ? submit.getAttribute('data-label') ?? '' : submit.getAttribute('data-label-sold-out') ?? ''
    );
  }
}

/**
 * Schreibt die Beschriftung und hält den Pfeil dabei als eigenes Element.
 *
 * ⚠️ Vorher stand hier `submit.textContent = …`. Das ersetzt den gesamten
 * Inhalt — also auch das `<span class="arr">`, das `snippets/label-arrow.liquid`
 * beim Rendern erzeugt. Der Pfeil bewegte sich dann bis zum ersten Chip-Klick
 * und danach nie wieder: kein Fehler, keine Meldung, nur eine Animation, die
 * plötzlich fehlt. Genau die Sorte Fund, die im Browser eine Stunde kostet.
 *
 * Bewusst über DOM-Knoten statt `innerHTML`: Die Beschriftung kommt aus einem
 * Setting, und ein Setting gehört nicht als Markup in die Seite.
 *
 * @param {HTMLElement} element
 * @param {string} label
 */
function setLabel(element, label) {
  element.replaceChildren();

  const arrowAt = label.indexOf('→');
  if (arrowAt === -1) {
    element.textContent = label;
    return;
  }

  // Das Leerzeichen vor dem Pfeil faellt weg — den Zwischenraum macht `gap`.
  const before = label.slice(0, arrowAt).replace(/\s+$/, '');
  const after = label.slice(arrowAt + 1).replace(/^\s+/, '');
  if (before !== '') element.append(before);

  const arrow = document.createElement('span');
  arrow.className = 'arr';
  arrow.setAttribute('aria-hidden', 'true');
  arrow.textContent = '→';
  element.append(arrow);

  if (after !== '') element.append(after);
}

if (!customElements.get('buy-box')) {
  customElements.define('buy-box', BuyBox);
}
